import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { formatNumberToDisplay } from "@/lib/api/common";
import { generateDisplayLabelForOtcDetailedType } from "./common";

export function getGeneralDetailColumns({ meta, commodityDisplayName, contractTypeLookupByNumber, customerNameMap }: { commodityDisplayName: string, contractTypeLookupByNumber?: (contract_number?: string) => string | undefined; customerNameMap?: Record<string, string>; meta?: any }) {
    const columnHelper = createColumnHelper<any>();
    
    return [
        columnHelper.accessor("customer_id", {
            id: "customer_id",
            header: "Customer",
            meta,
            ...({
                accessorFn: (row: any) => {
                    return (
                            row.Customer?.long_name || row.report_customer_id
                    )
                }
            }),
            filterFn: "includesString",
            footer: (props: any) => props.column.id,
        }),
        columnHelper.accessor("commodity", {
            id: "commodity",
            header: "Commodity",
            footer: (props: any) => props.column.id,
            meta,
        }),
        columnHelper.accessor("crop_year", {
            id: "crop_year",
            header: "Crop Year",
            footer: (props: any) => props.column.id,
            meta,
        }),
        columnHelper.accessor("contract_type", {
            id: "contract_type",
            header: "Contract Type",
            ...({
                accessorFn: (row: any) => {
                const lookedupContractType = contractTypeLookupByNumber ? contractTypeLookupByNumber(row.contract_number) : undefined;
                return generateDisplayLabelForOtcDetailedType(row.detailed_contract_type) || generateDisplayLabelForOtcDetailedType(lookedupContractType || row.contract_type)
            }}),
            meta,

            footer: (props: any) => props.column.id,
        }),
        columnHelper.accessor("contract_number", {
            id: "contract_number",
            header: "Contract Number",
            meta,
        }),
        columnHelper.accessor("contract_release", {
            id: "contract_release",
            header: "Rel",
            meta,
        }),
        columnHelper.accessor("is_open", {
            id: "is_open",
            header: "Open",
            meta,
            filterFn: (row, columnId, filterValue) => {
                if (!filterValue) return true;

                if ('open'.startsWith(filterValue?.toLocaleLowerCase())) {
                    return row.getValue(columnId) === true;
                }
                return 'closed'.startsWith(filterValue?.toLocaleLowerCase()) && row.getValue(columnId) === false;
            },
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {info.getValue() ? "Open" : "Closed"}
                    </span>
                )
            },
        }),
        columnHelper.accessor("option_month", {
            id: "option_month",
            header: "Option Month",
            meta,
        }),
        columnHelper.accessor("contract_date", {
            id: "contract_date",
            header: "Contract Date",
            sortingFn: "datetime",
            meta,
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {info.getValue() ? moment.tz(info.getValue(), CommonDestinationTimezone).format("MM/DD/YYYY") : ''}
                    </span>
                )
            },
        }),
        columnHelper.accessor("quantity", {
            id: "quantity",
            header: "Quantity",
            meta,
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {formatNumberToDisplay(info.getValue())}
                    </span>
                )
            },
        }),
        columnHelper.accessor("balance", {
            id: "balance",
            header: "Balance",
            meta,
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {formatNumberToDisplay(info.getValue())}
                    </span>
                )
            },
        }),
        columnHelper.accessor("futures", {
            id: "futures",
            header: "Futures",
            meta,
        }),
        columnHelper.accessor("basis", {
            id: "basis",
            header: "Basis",
            meta,
        }),
        columnHelper.accessor("implied_cost", {
            id: "implied_cost",
            header: "Revenue",
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {formatNumberToDisplay(info.getValue())}
                    </span>
                )
            },
            meta,
        }),
        columnHelper.accessor("implied_futures", {
            id: "implied_futures",
            header: "Implied Futures",
            meta,
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {formatNumberToDisplay(info.getValue() as string || "0", { maximumFractionDigits: 4 })}
                    </span>
                )
            },
        }),
        columnHelper.accessor("implied_basis", {
            id: "implied_basis",
            header: "Implied Basis",
            meta,
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {formatNumberToDisplay(info.getValue() as string || "0", { maximumFractionDigits: 4 })}
                    </span>
                )
            },
        }),
        columnHelper.accessor("implied_fx_futures", {
            id: "implied_fx_futures",
            header: "Implied FX Futures",
            meta,
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {formatNumberToDisplay(info.getValue() as string || "0", { maximumFractionDigits: 4 })}
                    </span>
                )
            },
        }),
        columnHelper.accessor("implied_fx_futures_contract", {
            id: "implied_fx_futures_contract",
            header: "Implied Fx Futures Contract",
            meta,
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {info.getValue() as string || "--"}
                    </span>
                )
            },
        }),
        columnHelper.accessor("status", {
            id: "status",
            header: "Status",
            meta,
            ...({
                accessorFn: (row: any) => (row.status)
            }),
        }),
        columnHelper.accessor("market_zone_id", {
            id: "market_zone_id",
            header: "Market Zone",
            meta,
            ...({
                accessorFn: (row: any) => (row.MarketZone?.dtn_market_zone || row.report_market_zone_id)
            }),
            footer: (props: any) => props.column.id,
        }),

        columnHelper.accessor("start_date", {
            id: "start_date",
            header: "Start Date",
            sortingFn: "datetime",
            filterFn: "dateTimeFilter" as any,
            meta,
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {info.getValue() ? moment.tz(info.getValue(), CommonDestinationTimezone).format("MM/DD/YYYY") : ''}
                    </span>
                )
            },
        }),
        columnHelper.accessor("end_date", {
            id: "end_date",
            header: "End Date",
            meta,
            sortingFn: "datetime",
            filterFn: "dateTimeFilter" as any,
            cell: (info) => {
                return (
                    <span className="text-gray-600 text-sm">
                        {info.getValue() ? moment.tz(info.getValue(), CommonDestinationTimezone).format("MM/DD/YYYY") : ''}
                    </span>
                )
            },
        }),
        columnHelper.accessor("location_id", {
            id: "location_id",
            header: "Location",
            meta,
            ...({
                accessorFn: (row: any) => (row.Location?.name || row.report_location_id)
            }),
            footer: (props: any) => props.column.id,
        }),
        columnHelper.accessor("trader_id", {
            id: "trader_id",
            header: "Trader",
            meta,
            ...({
                accessorFn: (row: any) => (row.Trader?.name || row.report_trader_id)
            }),
            footer: (props: any) => props.column.id,
        }),
    ].filter(Boolean);
}
